import { useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { iArkStore, IArkComponent } from '@apps/i-ark';
import { getCaptionValue } from '@pm2/content-engine';
import { ElementToolbar } from '@pm2/element-toolbar';
import { scrollDirectionStore } from '@pm2/react-utils';
import { usePM2DisplayContext, pm2ConfigStore } from '@pm2/store';
import { usePm2Modal } from '@pm2/ui-lightbox';
import { componentFrameworkStore } from '@provider/component-framework';
import { useNavigationManager } from '@provider/navigation';
import { breakpoints } from '@ui/css';
import { useComponentEntry, UniversalLightboxCard, UniversalHarmonicaCard, UniversalFinishedCard } from '@utils/component-framework';
import { useMatchBreakpoint, useToggleState, SizeTransition } from '@utils/react';
import { EmbedContent } from './comp.embed-integration.js';
import { PM2IArkComponent } from './comp.root.js';
import { contentTransitionDurationMs, card, content } from './comp.router.style.js';
import { ScrambledCard } from './comp.scrambled.js';
import { jsx } from 'react/jsx-runtime';

function Router(props) {
  const navigation = useNavigationManager();
  const modal = usePm2Modal();
  const {
    inLightbox,
    inGrid
  } = usePM2DisplayContext();
  const resolvedDisplayMode = inGrid ? "lightbox" : props.displayMode;
  const badgeImage = pm2ConfigStore.useSelector(state => state.config.theme.componentPreviewImage);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const isEntryShared = useComponentEntry(entryKey, {
    select: entry => entry?.envelope.shared
  });
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const [expanded, expansionActions] = useToggleState();
  const state = (() => {
    if (props.isScrambled) {
      return "scrambled";
    }
    if (inLightbox) {
      return "standalone";
    }
    if (!expanded) {
      if (resolvedDisplayMode === "embed" && isEntryShared.data) {
        return "finished";
      }
      return resolvedDisplayMode;
    }
    return "embed";
  })();
  const onShareWithTeacher = useCallback(() => {
    expansionActions.disable();
    setTimeout(() => {
      // Reset to the initial path upon closing the component
      navigation.push("/");
    }, contentTransitionDurationMs);
  }, [expansionActions, navigation]);
  const onLightboxActivate = useCallback(() => {
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.activateComponent(props.node.component.envelope.ID));
  }, [props]);
  const onOpenLightbox = useCallback(() => {
    onLightboxActivate();
    modal.fromComponentFrameworkNode(/*#__PURE__*/jsx(PM2IArkComponent, {
      ...props,
      propagatedStore: props.propagatedStore
    }), "component-framework.i-ark");
  }, [modal, onLightboxActivate, props]);
  const onActivate = useCallback(() => {
    if (resolvedDisplayMode === "lightbox") {
      onOpenLightbox();
      return;
    }
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.activateComponent(props.node.component.envelope.ID));
    expansionActions.enable();

    // Force the header to collapse upon opening the component
    scrollDirectionStore.dispatch(scrollDirectionStore.actions.setDirection("down"));
  }, [expansionActions, onOpenLightbox, props.node.component.envelope.ID, resolvedDisplayMode]);
  const onDeactivate = useCallback(() => {
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.deactivateComponent(props.node.component.envelope.ID));
  }, [props.node.component.envelope.ID]);
  const onCollapse = useCallback(() => {
    expansionActions.disable();
    onDeactivate();
  }, [expansionActions, onDeactivate]);
  const content$1 = (() => {
    switch (state) {
      case "embed":
        return /*#__PURE__*/jsx(EmbedContent, {
          ...props,
          onActivate: onActivate,
          onDeactivate: onDeactivate,
          onShareWithTeacher: onShareWithTeacher,
          onOpenInLightbox: onOpenLightbox,
          onMountedInFullscreen: onLightboxActivate,
          onCollapse: resolvedDisplayMode === "harmonica" || isEntryShared.data && resolvedDisplayMode === "embed" ? onCollapse : undefined
        });
      case "standalone":
        return /*#__PURE__*/jsx(IArkComponent, {
          node: props.node,
          context: "lightbox",
          onMountedInFullscreen: onLightboxActivate
        });
      case "finished":
        return /*#__PURE__*/jsx(UniversalFinishedCard, {
          className: card,
          title: props.node.component.envelope.Title,
          onClick: onActivate,
          children: /*#__PURE__*/jsx(UniversalFinishedCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "harmonica":
        return /*#__PURE__*/jsx(UniversalHarmonicaCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title, "in-context"),
          description: props.richtextDescription,
          audience: audience,
          hasShared: isEntryShared.data,
          badgeImageSrc: badgeImage,
          indexOrLevel: props.indexOrLevel,
          onClick: isMobile ? onOpenLightbox : onActivate,
          children: /*#__PURE__*/jsx(UniversalHarmonicaCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "lightbox":
        return /*#__PURE__*/jsx(UniversalLightboxCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title, "in-context"),
          description: props.lightboxDescription,
          audience: audience,
          hasShared: isEntryShared.data,
          badgeImageSrc: badgeImage,
          previewImageSrc: props.previewImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onOpenLightbox,
          children: /*#__PURE__*/jsx(UniversalLightboxCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "scrambled":
        return /*#__PURE__*/jsx(ScrambledCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title, "in-context") ?? ""
        });
    }
  })();
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: /*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      swappable: true,
      classNames: content,
      timeout: contentTransitionDurationMs,
      delayMs: {
        enter: 10,
        exit: 0
      },
      onEnter: () => document.documentElement.style.setProperty("overflow-anchor", "none"),
      onEntered: () => document.documentElement.style.setProperty("overflow-anchor", ""),
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        "data-node-id": props.node.component.envelope.ID,
        className: content,
        children: content$1
      })
    }, state)
  });
}

export { Router };
//# sourceMappingURL=comp.router.js.map
